import React, { useState } from 'react';
import {
  GetAppointmentMediaOutput,
  useGetSignUrlFromStorageForRequesterQuery,
} from 'graphql/generated/hasura';
import ChatMediaModal from './ChatMediaModal';
import Default from 'assets/icons/mediaLibraryIcon.svg';
import { twMerge } from 'tailwind-merge';

interface PreworkSymptomsLocationProps {
  title: string;
  codexPatientId: string;
  symptoms: string[];
  symptomsMedia: GetAppointmentMediaOutput[] | null | undefined;
}

interface SymptomItemProps {
  location: string;
  description?: string;
  fileId?: string;
  codexPatientId: string;
}

const SymptomItem: React.FC<SymptomItemProps> = ({
  location,
  description,
  fileId,
  codexPatientId,
}) => {
  const output = location.replace(/^(.*?) - (.*?) - (.*?)$/, '$3 - $1');
  const [url, setUrl] = React.useState<string | undefined>();
  const [src, setSrc] = useState<string | null | undefined>(null);

  useGetSignUrlFromStorageForRequesterQuery({
    variables: {
      fileId: fileId ?? '',
      requestedUserId: codexPatientId,
    },
    onCompleted: (data) => {
      setUrl(data.GetSignUrlFromStorageForRequester?.url);
    },
  });

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <li
          className="flex flex-col desktop:flex-row items-start gap-2.5"
          key={output.toString()}
        >
          <div className="flex gap-2.5">
            <img
              className={twMerge(
                'w-[48px] h-[48px] object-cover rounded-5',
                url && 'cursor-pointer',
              )}
              src={url ?? Default}
              alt={`${location} image`}
              onClick={() => url && setSrc(url)}
            />
            <div className="text-med-gray-3">
              <p className="font-medium text-neutral-800">{output}</p>
              <p>{description}</p>
            </div>
          </div>
        </li>
      </div>
      <ChatMediaModal src={src} setSrc={setSrc} />
    </>
  );
};

export const PreworkSymptomsLocationSection: React.FC<
  PreworkSymptomsLocationProps
> = ({ title, symptoms, symptomsMedia, codexPatientId }) => {
  const set = new Set(symptoms);

  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-h7 font-bold uppercase">{title}</p>
      <ul className="text-med-gray flex flex-col gap-4 list-disc">
        {(symptomsMedia ?? []).map(({ bodySite, description, fileId }) => {
          if (!set.has(bodySite as string)) {
            return null;
          }
          return (
            <SymptomItem
              location={bodySite as string}
              description={description as string}
              fileId={fileId as string}
              codexPatientId={codexPatientId}
            />
          );
        })}
      </ul>
    </div>
  );
};
